import React, {useEffect, useState, useMemo} from "react"
import Link from "next/link"
import {Trans, Plural} from "@lingui/react/macro"
import styles from "./styles.module.css"
import ApiClient from "./apiClient"
import ArticleCard from "./ArticleCard"
import SearchBar from "./SearchBar"
import Pagination from "./Pagination"
import CategoryCard from "./CategoryCard"
import {buildCategoryUrl, buildHomeUrl} from "./utils"

const PER_PAGE = 18

function findCategoryNode(tree, aid) {
    if (!Array.isArray(tree)) return null
    for (const node of tree) {
        if (node.aid === aid) return {node, ancestors: []}
        const found = findCategoryNode(node.children, aid)
        if (found) return {node: found.node, ancestors: [node, ...found.ancestors]}
    }
    return null
}

export default function Category({context, langValue, categoryAid}) {
    const [tree, setTree] = useState([])
    const [articles, setArticles] = useState([])
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState("")
    const [searchDebounced, setSearchDebounced] = useState("")

    useEffect(() => {
        ApiClient.getCategories(langValue).then(d => setTree(d?.items || []))
    }, [langValue])

    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(search.trim()), 300)
        return () => clearTimeout(timer)
    }, [search])

    useEffect(() => { setPage(1) }, [searchDebounced, categoryAid])

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        ApiClient.listArticles({
            langValue, page, perPage: PER_PAGE,
            categoryAid, q: searchDebounced || null,
        }).then(d => {
            if (cancelled) return
            setArticles(d?.items || [])
            setTotal(d?.total || 0)
            setLoading(false)
        }).catch(() => !cancelled && setLoading(false))
        return () => { cancelled = true }
    }, [langValue, page, searchDebounced, categoryAid])

    const found = useMemo(() => findCategoryNode(tree, categoryAid), [tree, categoryAid])
    const node = found?.node
    const ancestors = found?.ancestors || []
    const subCategories = node?.children || []
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

    return (
        <>
            {context.render !== "embed" && <nav className={styles.breadcrumb}>
                <Link href={buildHomeUrl(context)} passHref legacyBehavior>
                    <a className={styles.breadcrumbLink}><Trans>Explorer la bibliothèque</Trans></a>
                </Link>
                {ancestors.map(a => (
                    <React.Fragment key={a.aid}>
                        <span className={styles.breadcrumbSep}>›</span>
                        <Link href={buildCategoryUrl(context, a.aid)} passHref legacyBehavior>
                            <a className={styles.breadcrumbLink}>{a.title}</a>
                        </Link>
                    </React.Fragment>
                ))}
                {node && (
                    <>
                        <span className={styles.breadcrumbSep}>›</span>
                        <span className={styles.breadcrumbCurrent}>{node.title}</span>
                    </>
                )}
            </nav>}

            <section className={styles.hero}>
                <h1 className={styles.heroTitle}>{node?.title || <Trans>Catégories</Trans>}</h1>
                {total > 0 && (
                    <p className={styles.heroSubtitle}>
                        <Plural value={total} one="# article" other="# articles"/>
                    </p>
                )}
                <SearchBar value={search} onChange={setSearch}/>
            </section>

            {subCategories.length > 0 && (
                <section className={styles.section} style={{marginTop: 0, marginBottom: 32}}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}><Trans>Catégories</Trans></h2>
                    </div>
                    <div className={styles.categoriesGrid}>
                        {subCategories.map(sub => (
                            <CategoryCard
                                key={sub.aid}
                                context={context}
                                category={sub}
                            />
                        ))}
                    </div>
                </section>
            )}

            {loading ? (
                <div className={styles.articlesGrid}>
                    {Array.from({length: 9}).map((_, i) => (
                        <div key={i} className={`${styles.skeleton} ${styles.skeletonCard}`}/>
                    ))}
                </div>
            ) : articles.length === 0 ? (
                <div className={styles.empty}>
                    <div className={styles.emptyIcon}>📭</div>
                    <p><Trans>Aucun article trouvé.</Trans></p>
                </div>
            ) : (
                <>
                    <div className={styles.articlesGrid}>
                        {articles.map(a => (
                            <ArticleCard key={a.aid} context={context} article={a}/>
                        ))}
                    </div>
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={(p) => { setPage(p); window.scrollTo({top: 0, behavior: "smooth"}) }}
                    />
                </>
            )}
        </>
    )
}
